import { createRouter, createWebHistory, RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from "vue-router";
import Main from "@/views/Main.vue";
import Login from "@/components/pages/Login.vue";
import Store from "@/store";
import { AuthApi } from "@/autogen/api";
import { toClientPath } from "../helpers/path-utils";
import * as RoleHelper from "../helpers/role-helper";
import { RoleName, expandRole } from "@/types";

const DEFAULT_TITLE = "HYOUMANBOX";
const routes: Array<RouteRecordRaw> = [
  {
    name: "Login",
    path: "/login",
    component: Login
  },
  {
    name: "ForgotPassword",
    path: "/forgot_password",
    component: () => import("@/components/pages/ForgotPassword.vue"),
    props: true
  },
  {
    name: "ResetPassword",
    path: "/reset_password",
    component: () => import("@/components/pages/ResetPassword.vue"),
    props: true
  },
  {
    name: "Print",
    path: "/print",
    component: () => import("@/components/atoms/Print.vue"),
    props: true
  },
  {
    name: "NotFound",
    path: "/notfound",
    component: () => import("@/components/pages/NotFound.vue"),
    props: true
  },
  {
    path: "/",
    component: Main,
    children: [
      {
        name: "Dashboard",
        path: "clients/:clientId/departments/:departmentId/dashboard",
        component: () => import("@/components/pages/Dashboard.vue"),
        props: true
      },
      {
        name: "Employee",
        path: "clients/:clientId/departments/:departmentId/employees",
        component: () => import("@/components/pages/Employees.vue"),
        props: true
      },
      {
        name: "EmployeeStats",
        path: "clients/:clientId/employees/:peopleId",
        component: () => import("@/components/pages/People.vue"),
        props: true
      },
      {
        name: "Applicant",
        path: "clients/:clientId/applicants",
        component: () => import("@/components/pages/Applicants.vue"),
        props: true
      },
      {
        name: "ApplicantStats",
        path: "clients/:clientId/applicants/:peopleId",
        component: () => import("@/components/pages/People.vue"),
        props: true
      },
      {
        name: "Clients",
        path: "system",
        component: () => import("@/components/pages/Clients.vue"),
        props: true
      },
      {
        name: "Users",
        path: "clients/:clientId/users",
        component: () => import("@/components/pages/Users.vue"),
        props: true
      }
    ]
  },
  {
    name: "Analysis",
    path: "/clients/:clientId/analysis",
    component: Main,
    children: [
      {
        path: "",
        redirect: to => {
          const path = toClientPath(to.fullPath);
          return `/clients/${path.clientId}/analysis/rd`;
        }
      },
      {
        name: "RDAnalysis",
        path: "rd",
        component: () => import("@/components/pages/RDAnalytics.vue"),
        props: true
      },
      {
        name: "PDAnalysis",
        path: "pd",
        component: () => import("@/components/pages/PDAnalytics.vue"),
        props: true
      },
      {
        name: "PMAnalytics",
        path: "pm",
        component: () => import("@/components/pages/PMAnalytics.vue"),
        props: true
      },
      {
        name: "HSAnalytics",
        path: "hs",
        component: () => import("@/components/pages/HSAnalytics.vue"),
        props: true
      },
      {
        name: "PersonalityMatrix",
        path: "personalityMatrix",
        component: () => import("@/components/pages/PersonalityMatrix.vue"),
        props: true
      },
      {
        name: "Progress",
        path: "progress",
        component: () => import("@/components/pages/Progress.vue"),
        props: true
      }
    ]
  },
  {
    name: "AsBroadcast",
    path: "/clients/:clientId/as",
    component: Main,
    children: [
      {
        path: "",
        redirect: to => {
          const path = toClientPath(to.fullPath);
          return `/clients/${path.clientId}/as/group`;
        }
      },
      {
        name: "ASGroup",
        path: "group",
        component: () => import("@/components/pages/as/Group.vue"),
        props: true
      },
      {
        name: "ASDetail",
        path: "group/:asBroadcastId",
        component: () => import("@/components/pages/as/Detail.vue"),
        props: true
      }
    ]
  },
  {
    name: "Manage",
    path: "/clients/:clientId/organization",
    component: Main,
    children: [
      {
        path: "",
        redirect: to => {
          const path = toClientPath(to.fullPath);
          return `/clients/${path.clientId}/organization/departments`;
        }
      },
      {
        name: "Departments",
        path: "departments",
        component: () => import("@/components/pages/Departments.vue"),
        props: true
      },
      {
        name: "Authority",
        path: "authority",
        component: () => import("@/components/pages/Authority.vue"),
        props: true
      },
      {
        name: "Positions",
        path: "positions",
        component: () => import("@/components/pages/Positions.vue"),
        props: true
      },
      {
        name: "Exam",
        path: "exam",
        component: () => import("@/components/pages/ExamManage.vue"),
        props: true
      },
      {
        name: "MailTemplates",
        path: "mail_templates",
        component: () => import("@/components/pages/MailTemplates.vue"),
        props: true
      },
      {
        name: "AccessLogs",
        path: "access_logs",
        component: () => import("@/components/pages/AccessLogs.vue"),
        props: true
      },
      {
        name: "QueueManages",
        path: "queue_manages",
        component: () => import("@/components/pages/QueueManage.vue"),
        props: true
      }
    ]
  },
  {
    name: "SpecialManage",
    path: "/clients/:clientId/advance",
    component: Main,
    children: [
      {
        path: "",
        redirect: to => {
          const path = toClientPath(to.fullPath);
          return `/clients/${path.clientId}/advance/tags`;
        }
      },
      {
        name: "Tags",
        path: "tags",
        component: () => import("@/components/pages/Tags.vue"),
        props: true
      },
      {
        name: "HrCriteria",
        path: "hr_criteria",
        component: () => import("@/components/pages/HrCriteria.vue"),
        props: true
      },
      {
        name: "Retired",
        path: "retired",
        component: () => import("@/components/pages/Retired.vue"),
        props: true
      }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});
const publicPaths = ["/login", "/print", "/forgot_password", "/reset_password", "/notfound"];
router.beforeEach(async (to: RouteLocationNormalized, _from: RouteLocationNormalized, next: NavigationGuardNext) => {
  document.title = to.meta.title || DEFAULT_TITLE;
  if (to.matched.some(rec => publicPaths.includes(rec.path))) {
    return next();
  } else {
    const storeInstance = Store.getInstance();
    if (!storeInstance.getAuth()) {
      await new AuthApi()
        .authenticate({})
        .then(response => {
          storeInstance.setAuth(response.data);
        })
        .catch(() => {
          return next({ path: "/login", query: { redirect: to.fullPath } });
        });
    }

    const role = (await new AuthApi().getRole({})).data;
    storeInstance.setRole(role);
    const permissions: expandRole = role.permissions as expandRole;
    // for SystemAdmin
    if (role && role.id && parseInt(role.id) === parseInt(RoleName.SystemAdmin)) {
      await getInitial();
      return next();
    }

    // アクセス先の親router.nameを取得
    const toName = to.matched.filter(e => e.name).map(e => e.name!.toString().toLowerCase())[0];
    // ロールに存在しないがアクセスできるリスト
    if (to.fullPath === "/" || ["dashboard"].includes(toName)) {
      await getInitial();
      return next();
    }

    // ロール定義からアクセス先の定義を取得
    const roleList = RoleHelper.convertList.find(r => r.roleKey.toLowerCase() === toName);
    // テスト用　リリース前に削除
    if (!roleList) {
      console.error(roleList, "is undefined");
      router.push({ name: "NotFound", path: "/notfound" });
      return next();
    }
    if (permissions[roleList.roleKey]?.includes(roleList.checkType)) {
      await getInitial();
      return next();
    } else {
      router.push({ name: "NotFound", path: "/notfound" });
      return next();
    }
  }
});
const getInitial = async () => {
  const storeInstance = Store.getInstance();
  const dep = async () => await storeInstance.getDepartmentsApi({});
  const pos = async () => await storeInstance.getPositionsApi();
  const occ = async () => await storeInstance.getOccupationsApi();
  await Promise.all([dep(), pos(), occ()]).catch(e => {
    console.error(e);
  });
};
console.error = error => {
  if (/Loading chunk.*failed./i.test(error.message)) {
    window.location.reload();
  }
};

export default router;
